import * as React from "react";
import { Alert, Button, Card, Modal, notification, Select } from "antd";
import { request } from "../../services/cpln";
import { BillingAccountSummary } from "../../schema/types/account";
import { Org } from "../../schema/types/org/entity";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { FormItemValue } from "../../components/antd/form/FormItemValue";
import { CopyButtonWithApproval } from "../domain/create/copyButtonWithApproval";
import { TagLinksTable } from "../../components/detail/tagLinksTable";
import { tagLinkUrlPrefixes, getBillingErrorMessage } from "../../services/utils";
import { CardContent } from "../../components/antd/card/CardContent";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAccountSummaries, selectAccountSummaries } from "../../store/userDataSlice";
import { Link } from "react-router-dom";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpTextArea } from "../gvc/testNewForm/CpTextArea";

interface Props {
  org: Org;
  account: BillingAccountSummary | undefined;
  isLoadingAccount: boolean;
  updateAccount: () => Promise<void>;
}
export const Summary: React.FC<Props> = ({ org, account, isLoadingAccount, updateAccount }) => {
  const dispatch = useAppDispatch();

  const hasTagLinks =
    Object.entries(org.tags || {}).filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
      .length > 0;

  const [isLoadingChangeAccount, setIsLoadingChangeAccount] = React.useState(false);
  const [showChangingAccount, setShowChangingAccount] = React.useState(false);
  const [newAccountId, setNewAccountId] = React.useState("");

  React.useEffect(() => {
    process();
  }, []);

  async function process() {
    await dispatch(fetchAccountSummaries());
  }

  async function onAccountChange() {
    if (!account) {
      return;
    }

    setShowChangingAccount(true);
    setNewAccountId(account.id);
  }

  async function onConfirmAccountChange() {
    if (!account) {
      return;
    }
    try {
      setIsLoadingChangeAccount(true);
      await request({
        method: "post",
        service: "billing-ng",
        url: `/account/${account.id}/org/${org.name}`,
        body: {
          toAccountUUID: newAccountId,
        },
      });
      notification.success({
        message: "Success",
        description: "Changed account of the org",
      });
      setNewAccountId("");
      updateAccount();
    } catch (e) {
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
    } finally {
      setIsLoadingChangeAccount(false);
      setShowChangingAccount(false);
    }
  }

  const accountSummaries = useAppSelector(selectAccountSummaries);

  const showChangeAccountButton = account && accountSummaries.filter((acc) => acc.id !== account.id).length > 0;

  const showAlertEmail =
    !org.spec?.observability?.defaultAlertEmails || org.spec.observability.defaultAlertEmails.length < 1;

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardContent>
            <div>
              {showAlertEmail ? (
                <Alert
                  className="mb-4 w-fit"
                  type={`warning`}
                  showIcon
                  message="Alert Email is Not Set"
                  description={
                    <div>
                      Set default alert emails in{" "}
                      <Link to={`./../observability`} className="color-link">
                        Observability
                      </Link>{" "}
                      section to receive grafana alerts
                    </div>
                  }
                />
              ) : null}
              <div style={{ width: 450 }}>
                <FormItem>
                  <FormLabel>Account Name{account?.company ? ` | Company` : ""}</FormLabel>
                  {isLoadingAccount ? (
                    <div>Loading...</div>
                  ) : account ? (
                    <div className={`flex items-center ${showChangeAccountButton ? "mb-2" : ""}`}>
                      <Link to={`/home/account/${account.id}`} className="color-link">
                        {account.accountName || account.fullName || ""}
                        {account.company ? ` | ${account.company}` : ""}
                      </Link>
                      {showChangeAccountButton ? (
                        <Button className="ml-2" size={"small"} type={"primary"} onClick={onAccountChange}>
                          Change
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <div>No Access</div>
                  )}
                </FormItem>
              </div>
              <CpFormItem>
                <CpLabel name={`description`}>
                  <FormLabel>Description</FormLabel>
                </CpLabel>
                <CpTextArea autoSize name={`description`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`description`} />
              </CpFormItem>
              <FormItem lastItem={!hasTagLinks}>
                <FormLabel>Endpoint Prefix</FormLabel>
                {org.status?.endpointPrefix ? (
                  <div className="flex items-baseline gap-2">
                    <FormItemValue>
                      <div>{org.status?.endpointPrefix}</div>
                    </FormItemValue>
                    <CopyButtonWithApproval value={org.status?.endpointPrefix} />
                  </div>
                ) : (
                  <FormItemValue>No endpoint prefix is generated yet</FormItemValue>
                )}
              </FormItem>
              {hasTagLinks ? (
                <TagLinksTable
                  tableId="org-summary-tag-links"
                  tags={Object.entries(org.tags || {})
                    .filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
                    .map(([key, value]) => ({ key, value }))}
                  styles={{ container: { width: "100%" }, header: { marginBottom: 0 } }}
                />
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
      {!!account && showChangingAccount ? (
        <Modal
          open={showChangingAccount}
          title={"Change Account of the Org"}
          maskClosable={false}
          closable={false}
          onCancel={() => setShowChangingAccount(false)}
          onOk={onConfirmAccountChange}
          cancelButtonProps={{ disabled: isLoadingChangeAccount }}
          okButtonProps={{ disabled: isLoadingChangeAccount || !newAccountId || newAccountId === account?.id, loading: isLoadingChangeAccount }}
        >
          <FormItem>
            <FormLabel>Current Account Name</FormLabel>
            <FormItemValue>{account.accountName || account.fullName || ""}</FormItemValue>
          </FormItem>
          <FormItem>
            <FormLabel>New Account</FormLabel>
            <Select
              className="mb-4 w-full"
              showSearch
              value={newAccountId}
              onChange={setNewAccountId}
              options={accountSummaries.map((acc) => ({
                  label: acc.accountName || acc.fullName || acc.id,
                  value: acc.id,
                  fullName: acc.fullName,
                  accountName: acc.accountName,
                }))}
              optionRender={(option) => (
                <div className="py-2">
                  {option.data.accountName ? (
                    <>
                      <div className="-mb-1 text-xs color-label">Account Name</div>
                      <div className="font-semibold">{option.data.accountName}</div>
                    </>
                  ) : null}
                  {option.data.fullName && option.data.fullName !== option.data.accountName ? (
                    <>
                      <div className="-mb-1 text-xs color-label">Full Name</div>
                      <div className="font-semibold">{option.data.fullName}</div>
                    </>
                  ) : null}
                  <div className="-mb-1 text-xs color-label">Account ID</div>
                  <div className="font-semibold">{option.value}</div>
                </div>
              )}
              filterOption={(inputValue, option) => {
                const as = accountSummaries.find((as) => as.id === option?.value);
                if (!as) {
                  return false;
                }
                const value = inputValue.toLowerCase();
                if (as.accountName?.toLowerCase().includes(value)) {
                  return true;
                }
                if (as.fullName?.toLowerCase().includes(value)) {
                  return true;
                }
                if (as.id?.toLowerCase().includes(value)) {
                  return true;
                }
                return false;
              }}
            />
          </FormItem>
          {newAccountId ? (
            (() => {
              const selectedAccount = accountSummaries.find((as) => as.id === newAccountId);
              if (!selectedAccount) return null;
              return (
                <div className="mt-4 p-4 border rounded" style={{ borderColor: "var(--color-border)" }}>
                  {selectedAccount.fullName ? (
                    <div style={{ marginBottom: 8 }}>
                      <FormLabel>Full Name</FormLabel>
                      <FormItemValue>{selectedAccount.fullName}</FormItemValue>
                    </div>
                  ) : null}
                  {selectedAccount.accountName ? (
                    <div style={{ marginBottom: 8 }}>
                      <FormLabel>Account Name</FormLabel>
                      <FormItemValue>{selectedAccount.accountName}</FormItemValue>
                    </div>
                  ) : null}
                  <div>
                    <FormLabel>Account ID</FormLabel>
                    <FormItemValue>{selectedAccount.id}</FormItemValue>
                  </div>
                </div>
              );
            })()
          ) : null}
        </Modal>
      ) : null}
    </>
  );
};
